/**
 * Energy, gym, nerve and drugs from the player's own log (website roadmap S12 — torn.report's Energy, Gym, Nerve and
 * Drugs pages), on the log-report kit (log-kit.ts; the browser half is apps/web/src/app/log-kit/). Pure and browser-safe;
 * tested in packages/shared/test/bars-report.test.ts.
 *
 * Torn doesn't document its log lines' `data`, so lines are read leniently: a line is placed by its title (and its type
 * where known), and amounts come from the line when it gives them, else from Torn's own item effects ("Increases energy
 * by 250") as the server's item list carries them, else from a small built-in copy of those effects. What can't be
 * known is left unknown (null), never guessed.
 */
import { GYM_STATS, readGymTrain, type GymStat } from './growth';
import { CRIME_TYPES, crimeById, readCrimeLogEntry, type CrimeLogEntry, type LogLike } from './crimes-report';

// ---------------------------------------------------------------- Torn's items that move the bars

/** What an item does to the bars (energy/nerve per use; `refill`: fills that bar to its maximum). */
export interface ItemBars {
  energy: number;
  nerve: number;
  happy: number;
  refill: 'energy' | 'nerve' | null;
}

export type ItemGroup = 'drug' | 'can' | 'alcohol' | 'booster' | 'candy' | 'other';

/** An item as the page knows it (the server's item list: name, Torn's type and effect text). */
export interface ItemRef {
  name: string;
  type?: string | null;
  effect?: string | null;
}

/** Torn's drugs, with the personal stat that counts each (Love Juice has none). */
export const DRUGS = [
  { id: 196, name: 'Cannabis', stat: 'cantaken' },
  { id: 197, name: 'Ecstasy', stat: 'exttaken' },
  { id: 198, name: 'Ketamine', stat: 'kettaken' },
  { id: 199, name: 'LSD', stat: 'lsdtaken' },
  { id: 200, name: 'Opium', stat: 'opitaken' },
  { id: 201, name: 'PCP', stat: 'pcptaken' },
  { id: 203, name: 'Shrooms', stat: 'shrtaken' },
  { id: 204, name: 'Speed', stat: 'spetaken' },
  { id: 205, name: 'Vicodin', stat: 'victaken' },
  { id: 206, name: 'Xanax', stat: 'xantaken' },
  { id: 870, name: 'Love Juice', stat: null },
] as const;
export const XANAX = 206;
export const ECSTASY = 197;

/**
 * Torn's own item effects for the items that move energy or nerve (as Torn's item list words them, 2026) — used only
 * when the server's item list isn't there. Energy drinks and alcohol are all in Torn's "Energy Drink"/"Alcohol" types.
 */
const BUILT_IN: Record<number, { name: string; group: ItemGroup; effect: string }> = {
  196: { name: 'Cannabis', group: 'drug', effect: 'Increases nerve by 8-12.' },
  199: { name: 'LSD', group: 'drug', effect: 'Increases energy by 50, nerve by 5, and happiness by 200-500.' },
  203: { name: 'Shrooms', group: 'drug', effect: 'Increases happiness by 500 and reduces energy by 25.' },
  206: { name: 'Xanax', group: 'drug', effect: 'Increases energy by 250 and happiness by 75.' },
  985: { name: 'Can of Goose Juice', group: 'can', effect: 'Increases energy by 5.' },
  986: { name: 'Can of Damp Valley', group: 'can', effect: 'Increases energy by 10.' },
  987: { name: 'Can of Crocozade', group: 'can', effect: 'Increases energy by 15.' },
  530: { name: 'Can of Munster', group: 'can', effect: 'Increases energy by 20.' },
  553: { name: 'Can of Santa Shooters', group: 'can', effect: 'Increases energy by 20.' },
  532: { name: 'Can of Red Cow', group: 'can', effect: 'Increases energy by 25.' },
  554: { name: 'Can of Rockstar Rudolph', group: 'can', effect: 'Increases energy by 25.' },
  533: { name: 'Can of Taurine Elite', group: 'can', effect: 'Increases energy by 30.' },
  555: { name: 'Can of X-MASS', group: 'can', effect: 'Increases energy by 30.' },
  367: { name: 'Feathery Hotel Coupon', group: 'booster', effect: 'Refills energy. Increases happiness by 500.' },
  473: { name: 'Green Easter Egg', group: 'booster', effect: 'Increases energy by 500.' },
  474: { name: 'Red Easter Egg', group: 'booster', effect: 'Increases nerve by 250.' },
  180: { name: 'Bottle of Beer', group: 'alcohol', effect: 'Increases nerve by 1.' },
  181: { name: 'Bottle of Champagne', group: 'alcohol', effect: 'Increases nerve by 1.' },
  294: { name: 'Bottle of Sake', group: 'alcohol', effect: 'Increases nerve by 1.' },
  426: { name: 'Bottle of Tequila', group: 'alcohol', effect: 'Increases nerve by 1.' },
  531: { name: 'Bottle of Pumpkin Brew', group: 'alcohol', effect: 'Increases nerve by 2.' },
  550: { name: 'Bottle of Kandy Kane', group: 'alcohol', effect: 'Increases nerve by 2.' },
  816: { name: 'Glass of Beer', group: 'alcohol', effect: 'Increases nerve by 2.' },
  542: { name: 'Bottle of Wicked Witch', group: 'alcohol', effect: 'Increases nerve by 3.' },
  551: { name: 'Bottle of Minty Mayhem', group: 'alcohol', effect: 'Increases nerve by 3.' },
  638: { name: 'Bottle of Christmas Cocktail', group: 'alcohol', effect: 'Increases nerve by 3.' },
  541: { name: 'Bottle of Stinky Swamp Punch', group: 'alcohol', effect: 'Increases nerve by 4.' },
  552: { name: 'Bottle of Mistletoe Madness', group: 'alcohol', effect: 'Increases nerve by 4.' },
  873: { name: 'Bottle of Green Stout', group: 'alcohol', effect: 'Increases nerve by 5.' },
  924: { name: 'Bottle of Christmas Spirit', group: 'alcohol', effect: 'Increases nerve by 5.' },
  984: { name: 'Bottle of Moonshine', group: 'alcohol', effect: 'Increases nerve by 5.' },
};

const n = (x: unknown): number | null => {
  const v = typeof x === 'string' && /^-?[\d,]+(\.\d+)?$/.test(x.trim()) ? Number(x.replace(/,/g, '')) : x;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

/**
 * Torn's effect text → what one use does to the bars: "Increases energy by 250", "reduces energy by 25", "nerve by 8-12"
 * (the middle: 10), "Refills energy". Each amount takes its sign from the nearest "increases"/"reduces" before it.
 */
export function itemBars(effect: string | null | undefined): ItemBars {
  const text = (effect ?? '').toLowerCase();
  const out: ItemBars = { energy: 0, nerve: 0, happy: 0, refill: /refills? (?:your )?energy/.test(text) ? 'energy' : /refills? (?:your )?nerve/.test(text) ? 'nerve' : null };
  for (const m of text.matchAll(/\b(energy|nerve|happiness) by ([\d,]+)(?:\s*-\s*([\d,]+))?/g)) {
    const before = text.slice(0, m.index);
    const sign = before.lastIndexOf('reduces') > before.lastIndexOf('increases') ? -1 : 1;
    const lo = n(m[2]) ?? 0;
    const hi = n(m[3]) ?? lo;
    const v = sign * ((lo + hi) / 2);
    if (m[1] === 'energy') out.energy = v;
    else if (m[1] === 'nerve') out.nerve = v;
    else out.happy = v;
  }
  return out;
}

/** Torn's item type → our group. */
function groupOf(id: number, ref: ItemRef | undefined): ItemGroup {
  const t = (ref?.type ?? '').toLowerCase();
  if (t === 'drug' || DRUGS.some((d) => d.id === id)) return 'drug';
  if (t === 'energy drink') return 'can';
  if (t === 'alcohol') return 'alcohol';
  if (t === 'booster') return 'booster';
  if (t === 'candy') return 'candy';
  return BUILT_IN[id]?.group ?? 'other';
}

/** An item's name, group and bar effects: the server's item list first, then the built-in copy. */
export function itemInfo(id: number, items?: ReadonlyMap<number, ItemRef>): { name: string; group: ItemGroup; bars: ItemBars } {
  const ref = items?.get(id);
  const effect = ref?.effect ?? BUILT_IN[id]?.effect ?? null;
  return { name: ref?.name ?? BUILT_IN[id]?.name ?? DRUGS.find((d) => d.id === id)?.name ?? `Item ${id}`, group: groupOf(id, ref), bars: itemBars(effect) };
}

// ---------------------------------------------------------------- a log line → what it did to the bars

export type BarKind = 'item' | 'overdose' | 'refill' | 'gym' | 'attack' | 'crime' | 'bust' | 'other';

export interface BarLine {
  id: string;
  at: number;
  title: string;
  kind: BarKind;
  /** What it's summed under ("Xanax", "Energy refill", "Gym: strength", "Attacks", a crime's name…). */
  label: string;
  /** Energy gained (> 0) or spent (< 0); null: the line doesn't say and Torn's rules don't fix it. */
  energy: number | null;
  nerve: number | null;
  /** Items: which, and its group. */
  item?: number;
  group?: ItemGroup;
  /** Gym trains. */
  stat?: GymStat;
  gain?: number;
  trains?: number;
  happy?: number | null;
  /** Crimes (Crimes 2.0, as the crimes report reads them). */
  crime?: CrimeLogEntry;
  /** Busts: did it work. */
  success?: boolean;
}

export interface BarContext {
  /** The server's item list (name, type, effect). */
  items?: ReadonlyMap<number, ItemRef>;
  /** Crime names → crime type (crimeNameIndex). */
  crimeNames?: ReadonlyMap<string, number>;
  /** Your bars' maximums now (a refill fills to them when the line gives no amount). */
  maxEnergy?: number | null;
  maxNerve?: number | null;
}

/** Torn's energy for an attack (Love Juice lowers it; the line's own figure wins). */
export const ATTACK_ENERGY = 25;

const firstNum = (d: Record<string, unknown>, keys: readonly string[]): number | null => {
  for (const k of keys) {
    const v = n(d[k]);
    if (v !== null) return v;
  }
  return null;
};

/** The item a line names: data.item / item_id / items_used ({ "206": 1 } or [206]). */
function itemOf(d: Record<string, unknown>): { id: number; amount: number } | null {
  const one = firstNum(d, ['item', 'item_id', 'itemid']);
  if (one !== null && one > 0) return { id: one, amount: Math.max(1, firstNum(d, ['quantity', 'qty', 'amount']) ?? 1) };
  for (const k of ['items', 'items_used', 'item_used']) {
    const v = d[k];
    if (Array.isArray(v) && n(v[0]) !== null) return { id: n(v[0])!, amount: v.length };
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const [first] = Object.entries(v as Record<string, unknown>);
      if (first && n(first[0])) return { id: n(first[0])!, amount: Math.max(1, n(first[1]) ?? 1) };
    }
  }
  return null;
}

/** A log line → what it did to energy and nerve, or null when it did nothing the page counts. */
export function readBarLine(l: LogLike, ctx: BarContext = {}): BarLine | null {
  const d = l.data ?? {};
  const title = l.title.trim();
  const t = title.toLowerCase();
  const base = { id: l.id, at: l.at, title };

  const gym = readGymTrain({ log: l.type, title, data: d });
  if (gym) return { ...base, kind: 'gym', label: `Gym: ${gym.stat}`, energy: -gym.energy, nerve: null, stat: gym.stat, gain: gym.gain, trains: gym.trains, happy: gym.happy };

  const crime = readCrimeLogEntry(l, ctx.crimeNames ?? new Map());
  if (crime) return { ...base, kind: 'crime', label: crime.typeId !== null ? (crimeById(crime.typeId)?.name ?? crime.crime) : crime.crime || 'Not named in the log', energy: null, nerve: -Math.abs(crime.nerve), crime };

  if (/\brefill\b/.test(t) && /energy|nerve/.test(t) && !/token/.test(t)) {
    const bar = /nerve/.test(t) ? 'nerve' : 'energy';
    const said = firstNum(d, [bar, `${bar}_gained`, `${bar}_increased`, 'amount']);
    const amount = said ?? (bar === 'energy' ? ctx.maxEnergy : ctx.maxNerve) ?? null;
    return { ...base, kind: 'refill', label: bar === 'energy' ? 'Energy refill' : 'Nerve refill', energy: bar === 'energy' ? amount : null, nerve: bar === 'nerve' ? amount : null };
  }

  const it = itemOf(d);
  if (/overdos/.test(t)) {
    const info = it ? itemInfo(it.id, ctx.items) : null;
    return { ...base, kind: 'overdose', label: info?.name ?? 'Overdose', energy: null, nerve: null, ...(it ? { item: it.id, group: info!.group } : {}) };
  }
  if (it && /\buse|used\b|consume|drink|eat|take|taken/.test(t)) {
    const info = itemInfo(it.id, ctx.items);
    const said = { energy: firstNum(d, ['energy', 'energy_gained', 'energy_increased']), nerve: firstNum(d, ['nerve', 'nerve_gained', 'nerve_increased']) };
    const energy = said.energy ?? (info.bars.refill === 'energy' ? (ctx.maxEnergy ?? null) : info.bars.energy * it.amount);
    const nerve = said.nerve ?? (info.bars.refill === 'nerve' ? (ctx.maxNerve ?? null) : info.bars.nerve * it.amount);
    if (info.group === 'other' && !energy && !nerve) return null;
    return { ...base, kind: 'item', label: info.name, energy, nerve, item: it.id, group: info.group };
  }

  // Attacks you made (not the ones on you): Torn's energy per attack unless the line says.
  if (/^attack/.test(t) && !/receive|defend|against you|\bby\b/.test(t)) {
    return { ...base, kind: 'attack', label: 'Attacks', energy: -Math.abs(firstNum(d, ['energy_used', 'energy']) ?? ATTACK_ENERGY), nerve: null };
  }
  if (/\bbust/.test(t) && !/receive|\bby\b/.test(t)) {
    const nerve = firstNum(d, ['nerve_used', 'nerve']);
    return { ...base, kind: 'bust', label: 'Busts', energy: null, nerve: nerve === null ? null : -Math.abs(nerve), success: /success/.test(t) };
  }
  // Anything else that says it used energy or nerve (revives, …), by its own title.
  const e = firstNum(d, ['energy_used', 'energy_spent']);
  const nv = firstNum(d, ['nerve_used', 'nerve_spent']);
  if ((e !== null && e !== 0) || (nv !== null && nv !== 0)) return { ...base, kind: 'other', label: title, energy: e === null ? null : -Math.abs(e), nerve: nv === null ? null : -Math.abs(nv) };
  return null;
}

// ---------------------------------------------------------------- summing up

export interface FlowRow {
  label: string;
  /** How many lines. */
  count: number;
  /** Sum of the known amounts (positive both ways: what came in, or what went out). */
  amount: number;
  /** Lines whose amount isn't known. */
  unknown: number;
}

export interface Flow {
  in: FlowRow[];
  out: FlowRow[];
  totalIn: number;
  totalOut: number;
  /**
   * What the log doesn't show coming in (natural regeneration, mostly — and anything Torn doesn't log): what went out
   * minus what the log shows coming in, when that's above zero. Worked out, so only as good as the lines it reads.
   */
  rest: number;
}

function addTo(rows: Map<string, FlowRow>, label: string, v: number | null): void {
  const r = rows.get(label) ?? { label, count: 0, amount: 0, unknown: 0 };
  rows.set(label, r);
  r.count++;
  if (v === null) r.unknown++;
  else r.amount += Math.abs(v);
}

/** Energy or nerve in and out over lines: torn.report's Energy / Nerve "in" and "out" tables. */
export function barFlow(lines: readonly BarLine[], bar: 'energy' | 'nerve'): Flow {
  const ins = new Map<string, FlowRow>();
  const outs = new Map<string, FlowRow>();
  for (const l of lines) {
    const v = l[bar];
    if (l.kind === 'overdose') continue;
    if (v === null) {
      // Unknown amount: it still counts on the side it belongs to. An item's amount is unknown only when it fills this bar
      // to a maximum not read; a refill names its bar; a bust spends nerve.
      if (l.kind === 'item' || (l.kind === 'refill' && l.label.toLowerCase().startsWith(bar))) addTo(ins, l.label, null);
      else if (bar === 'nerve' && l.kind === 'bust') addTo(outs, l.label, null);
      continue;
    }
    if (v > 0) addTo(ins, l.label, v);
    else if (v < 0) addTo(outs, l.kind === 'gym' ? 'Gym' : l.label, v);
  }
  const sort = (m: Map<string, FlowRow>) => [...m.values()].sort((a, b) => b.amount - a.amount || b.count - a.count);
  const totalIn = [...ins.values()].reduce((a, r) => a + r.amount, 0);
  const totalOut = [...outs.values()].reduce((a, r) => a + r.amount, 0);
  return { in: sort(ins), out: sort(outs), totalIn, totalOut, rest: Math.max(0, totalOut - totalIn) };
}

// ---------------------------------------------------------------- gym

export interface GymSum {
  stat: GymStat | 'all';
  lines: number;
  trains: number;
  energy: number;
  gain: number;
  /** Gained per 100 energy (null: no energy). */
  per100: number | null;
}

/** Trains, energy, gains and gains per 100 energy for each stat and all together. */
export function gymSums(lines: readonly BarLine[]): GymSum[] {
  const rows = new Map<GymSum['stat'], GymSum>([...GYM_STATS, 'all' as const].map((s) => [s, { stat: s, lines: 0, trains: 0, energy: 0, gain: 0, per100: null }]));
  for (const l of lines) {
    if (l.kind !== 'gym' || !l.stat) continue;
    for (const r of [rows.get(l.stat)!, rows.get('all')!]) {
      r.lines++;
      r.trains += l.trains ?? 1;
      r.energy += Math.abs(l.energy ?? 0);
      r.gain += l.gain ?? 0;
    }
  }
  for (const r of rows.values()) r.per100 = r.energy > 0 ? (r.gain / r.energy) * 100 : null;
  return [...rows.values()];
}

export interface HappyJump {
  /** When the Ecstasy was taken (or the first train, for one found by its happiness). */
  at: number;
  /** How it was found: an Ecstasy before the trains, or the log's happiness. */
  by: 'ecstasy' | 'happy';
  trains: number;
  energy: number;
  gain: Record<GymStat, number>;
  gainAll: number;
  per100: number | null;
}

/** A jump's trains: within this long after the Ecstasy. */
export const JUMP_WINDOW = 2 * 3_600_000;
/** Happiness that marks a jump when the log gives happiness (a full bar is a few thousand without one). */
export const JUMP_HAPPY = 10_000;

/**
 * Happy jumps (stacking happiness — Xanax, Ecstasy — then training): the gym trains within two hours after an Ecstasy, or
 * trains the log says were made at very high happiness. Each with its energy and gains, and gains per 100 energy to
 * compare with the usual (gymSums of the other trains).
 */
export function happyJumps(lines: readonly BarLine[]): { jumps: HappyJump[]; inJump: Set<string> } {
  const asc = [...lines].sort((a, b) => a.at - b.at);
  const jumps: HappyJump[] = [];
  const inJump = new Set<string>();
  const blank = (at: number, by: HappyJump['by']): HappyJump => ({ at, by, trains: 0, energy: 0, gain: { strength: 0, defense: 0, speed: 0, dexterity: 0 }, gainAll: 0, per100: null });
  let open: HappyJump | null = null;
  let until = 0;
  for (const l of asc) {
    if (l.kind === 'item' && l.item === ECSTASY) {
      open = blank(l.at, 'ecstasy');
      until = l.at + JUMP_WINDOW;
      jumps.push(open);
      continue;
    }
    if (l.kind !== 'gym' || !l.stat) continue;
    let j: HappyJump | null = open && l.at <= until ? open : null;
    if (!j && l.happy !== null && l.happy !== undefined && l.happy >= JUMP_HAPPY) {
      const last = jumps.at(-1);
      j = last && last.by === 'happy' && l.at - last.at <= JUMP_WINDOW ? last : blank(l.at, 'happy');
      if (j !== last) jumps.push(j);
    }
    if (!j) continue;
    inJump.add(l.id);
    j.trains += l.trains ?? 1;
    j.energy += Math.abs(l.energy ?? 0);
    j.gain[l.stat] += l.gain ?? 0;
    j.gainAll += l.gain ?? 0;
  }
  const done = jumps.filter((j) => j.trains > 0);
  for (const j of done) j.per100 = j.energy > 0 ? (j.gainAll / j.energy) * 100 : null;
  return { jumps: done.sort((a, b) => b.at - a.at), inJump };
}

// ---------------------------------------------------------------- nerve

export interface NerveRow {
  key: string;
  label: string;
  attempts: number;
  nerve: number;
}

/** Crime skill bands for "nerve by skill" (your skill in that crime now). */
export const SKILL_BANDS = [
  { label: 'Skill 100', min: 100 },
  { label: '75–99', min: 75 },
  { label: '50–74', min: 50 },
  { label: '25–49', min: 25 },
  { label: '0–24', min: 0 },
] as const;

/**
 * Nerve spent on crimes by crime, by outcome and by your skill in the crime now (`skills`: crime type → skill, from
 * personal stats); busts on their own row.
 */
export function nerveBreakdown(lines: readonly BarLine[], skills: ReadonlyMap<number, number> = new Map()): { byCrime: NerveRow[]; byOutcome: NerveRow[]; bySkill: NerveRow[]; busts: { attempts: number; success: number; nerve: number; unknown: number } } {
  const crime = new Map<string, NerveRow>();
  const outcome = new Map<string, NerveRow>();
  const skill = new Map<string, NerveRow>();
  const busts = { attempts: 0, success: 0, nerve: 0, unknown: 0 };
  const add = (m: Map<string, NerveRow>, key: string, label: string, v: number) => {
    const r = m.get(key) ?? { key, label, attempts: 0, nerve: 0 };
    m.set(key, r);
    r.attempts++;
    r.nerve += v;
  };
  const OUT: Record<string, string> = { success: 'Success', fail: 'Fail', critical: 'Critical fail', other: 'Other' };
  for (const l of lines) {
    if (l.kind === 'bust') {
      busts.attempts++;
      if (l.success) busts.success++;
      if (l.nerve === null) busts.unknown++;
      else busts.nerve += Math.abs(l.nerve);
      continue;
    }
    if (l.kind !== 'crime' || !l.crime) continue;
    const v = Math.abs(l.nerve ?? 0);
    const c = l.crime;
    add(crime, c.typeId !== null ? String(c.typeId) : `name:${l.label}`, l.label, v);
    add(outcome, c.outcome, OUT[c.outcome] ?? c.outcome, v);
    const s = c.typeId !== null ? skills.get(c.typeId) : undefined;
    const band = s === undefined ? null : SKILL_BANDS.find((b) => s >= b.min)!;
    add(skill, band?.label ?? 'unknown', band?.label ?? 'Skill not known', v);
  }
  const order = (m: Map<string, NerveRow>) => [...m.values()].sort((a, b) => b.nerve - a.nerve || b.attempts - a.attempts);
  const bandOrder = (r: NerveRow) => SKILL_BANDS.findIndex((b) => b.label === r.label);
  return {
    byCrime: order(crime),
    byOutcome: order(outcome),
    bySkill: [...skill.values()].sort((a, b) => (bandOrder(a) < 0 ? 99 : bandOrder(a)) - (bandOrder(b) < 0 ? 99 : bandOrder(b))),
    busts,
  };
}

/** Crime type ids your skill puts at 100 (for the "Skill 100 only" view). */
export const skill100 = (skills: ReadonlyMap<number, number>): Set<number> => new Set(CRIME_TYPES.filter((c) => (skills.get(c.id) ?? 0) >= 100).map((c) => c.id));

// ---------------------------------------------------------------- drugs

export interface DrugRow {
  id: number;
  name: string;
  /** Uses in the lines. */
  uses: number;
  overdoses: number;
  last: number | null;
  /** Average time between two uses (ms; null: fewer than two). */
  gap: number | null;
  /** All time, from personal stats (null: Torn has no count for it, or not read). */
  allTime: number | null;
}

/** Per drug: uses and overdoses in the lines, last use, average time between uses, and the all-time count. */
export function drugRows(lines: readonly BarLine[], personal: Readonly<Record<string, number>> | null = null): DrugRow[] {
  const rows = new Map<number, DrugRow & { times: number[] }>();
  const row = (id: number, name: string) => {
    let r = rows.get(id);
    const stat = DRUGS.find((d) => d.id === id)?.stat ?? null;
    if (!r) rows.set(id, (r = { id, name, uses: 0, overdoses: 0, last: null, gap: null, allTime: stat && personal ? (personal[stat] ?? null) : null, times: [] }));
    return r;
  };
  for (const d of DRUGS) if (personal && d.stat && (personal[d.stat] ?? 0) > 0) row(d.id, d.name);
  for (const l of lines) {
    if (l.item === undefined || l.group !== 'drug') continue;
    const r = row(l.item, l.label);
    if (l.kind === 'overdose') r.overdoses++;
    else if (l.kind === 'item') {
      r.uses++;
      r.times.push(l.at);
      r.last = Math.max(r.last ?? 0, l.at);
    }
  }
  return [...rows.values()]
    .map(({ times, ...r }) => {
      const s = times.sort((a, b) => a - b);
      return { ...r, gap: s.length >= 2 ? (s.at(-1)! - s[0]!) / (s.length - 1) : null };
    })
    .sort((a, b) => b.uses - a.uses || (b.allTime ?? 0) - (a.allTime ?? 0) || a.id - b.id);
}

/** Lines → CSV rows (when, what, kind, energy, nerve, stat gained). */
export function barsCsvRows(lines: readonly BarLine[]): { header: string[]; rows: Array<Array<string | number | null>> } {
  return {
    header: ['time_utc', 'what', 'kind', 'energy', 'nerve', 'stat', 'gained', 'log_title'],
    rows: lines.map((l) => [new Date(l.at).toISOString(), l.label, l.kind, l.energy, l.nerve, l.stat ?? null, l.gain ?? null, l.title]),
  };
}
